import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Route, Routes, useNavigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider, useThemeControls } from "@/theme/ThemeProvider";
import { AuthProvider, useAuth } from "@/auth/AuthProvider";
import { ToastProvider } from "@/components/ToastProvider";
import { PaletteSwitcher } from "@/components/PaletteSwitcher";
import { useViewport } from "@/util/viewport";
import { useMe, useUpdateMe, type RoutineOut } from "@chess/shared";
import { LandingPage } from "@/pages/LandingPage";
import { LoginPage } from "@/pages/LoginPage";
import { ConnectAccountPage } from "@/pages/ConnectAccountPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { TrainingSelectPage } from "@/pages/TrainingSelectPage";
import { TrainDispatch } from "@/pages/TrainDispatch";
import { AnalysisPage } from "@/pages/AnalysisPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { SettingsModal } from "@/modals/SettingsModal";
import { DailyBuilderModal } from "@/modals/DailyBuilderModal";
import { UpgradeModal } from "@/modals/UpgradeModal";
import { RequireAuth } from "@/routes/RequireAuth";
import { AdminRoute } from "@/routes/AdminRoute";

// Lazy so non-admin bundles never download recharts.
const AdminDashboardPage = lazy(() =>
  import("@/pages/admin/AdminDashboardPage").then((m) => ({ default: m.AdminDashboardPage })),
);

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false } },
});

type ModalKind = "settings" | "builder" | "upgrade" | null;

function FloatingPalette({ hidden, persistRemote }: { hidden: boolean; persistRemote: boolean }) {
  const { themeId, setThemeId } = useThemeControls();
  const updateMe = useUpdateMe();
  const { isMobile } = useViewport();

  if (hidden) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: isMobile ? 12 : 20,
        right: isMobile ? 12 : 20,
        zIndex: 200,
      }}
    >
      <PaletteSwitcher
        themeId={themeId}
        onChange={(id) => {
          setThemeId(id);
          if (persistRemote) updateMe.mutate({ theme: id });
        }}
        compact={isMobile}
      />
    </div>
  );
}

function ServerThemeSync() {
  const { data: me } = useMe();
  const { setThemeId } = useThemeControls();
  useEffect(() => {
    if (me?.theme === "classic" || me?.theme === "soft" || me?.theme === "vice") {
      setThemeId(me.theme);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.theme]);
  return null;
}

function AppShell() {
  const [modal, setModal] = useState<ModalKind>(null);
  const [builderInitial, setBuilderInitial] = useState<RoutineOut | null>(null);
  const nav = useNavigate();

  const openSettings = () => setModal("settings");
  const openUpgrade = () => setModal("upgrade");
  const openBuilder = (initial?: RoutineOut | null) => {
    setBuilderInitial(initial ?? null);
    setModal("builder");
  };
  const startTraining = () => nav("/app/train");

  return (
    <>
      <ServerThemeSync />
      <Routes>
        <Route
          index
          element={<DashboardPage onOpenSettings={openSettings} onOpenBuilder={() => openBuilder(null)} onStartTraining={startTraining} />}
        />
        <Route
          path="train"
          element={<TrainingSelectPage onOpenSettings={openSettings} onOpenBuilder={(i) => openBuilder(i as RoutineOut | null)} onOpenUpgrade={openUpgrade} />}
        />
        <Route path="train/:sessionId" element={<TrainDispatch />} />
        <Route path="analysis/:gameId" element={<AnalysisPage onOpenSettings={openSettings} />} />
        <Route path="profile" element={<ProfilePage onOpenSettings={openSettings} />} />
      </Routes>

      <SettingsModal open={modal === "settings"} onClose={() => setModal(null)} onUpgrade={openUpgrade} />
      <DailyBuilderModal open={modal === "builder"} onClose={() => setModal(null)} initial={builderInitial} onUpgrade={openUpgrade} />
      <UpgradeModal open={modal === "upgrade"} onClose={() => setModal(null)} />
    </>
  );
}

function PublicShell({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  return (
    <>
      {children}
      <FloatingPalette hidden={false} persistRemote={auth.signedIn} />
    </>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <ToastProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/" element={<PublicShell><LandingPage /></PublicShell>} />
                <Route path="/login" element={<PublicShell><LoginPage /></PublicShell>} />
                <Route element={<RequireAuth />}>
                  <Route path="/connect" element={<ConnectAccountPage />} />
                  <Route element={<AdminRoute />}>
                    <Route
                      path="/app/admin"
                      element={
                        <Suspense fallback={<div style={{ padding: 24 }}>Loading…</div>}>
                          <AdminDashboardPage />
                        </Suspense>
                      }
                    />
                  </Route>
                  <Route path="/app/*" element={<AppShell />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

function NotFound() {
  return (
    <div style={{ padding: 40, fontFamily: "Space Grotesk, sans-serif", fontWeight: 800, fontSize: 24 }}>
      404 — page not found. <a href="/">Home</a>
    </div>
  );
}
